import type { ReactNode } from 'react';
import {
    Box,
    IconButton,
    MenuItem,
    Select,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { formatRange } from './format';
import { RESOLUTION_OPTIONS } from './resolution';
import {
    toolbarSelectSx,
    toolbarToggleSx,
} from '../../../components/common/page/toolbarStyles';

const AUTO = 'auto';

export interface QuickRangeButton {
    id: string;
    label: string;
    disabled: boolean;
    active: boolean;
}

interface MonitorToolbarProps {
    displayRange: readonly [Date, Date];
    zoomed: boolean;
    quickRanges: readonly QuickRangeButton[];
    onQuickRange: (id: string) => void;
    resolutionChoice: number | null;
    resolutionLabel: string;
    onResolutionChange: (choice: number | null) => void;
    fullscreen: boolean;
    onToggleFullscreen: () => void;
    controls?: ReactNode;
    children?: ReactNode;
}

export { toolbarToggleSx };

export function MonitorToolbar(props: MonitorToolbarProps) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                mb: 0.5,
            }}
        >
            {props.children}
            <Box sx={{ flex: 1 }} />
            <Typography
                sx={{
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    whiteSpace: 'nowrap',
                    color: props.zoomed ? 'text.primary' : 'text.secondary',
                    fontWeight: props.zoomed ? 600 : 400,
                }}
            >
                {formatRange(props.displayRange)}
            </Typography>
            <ToggleButtonGroup
                size="small"
                exclusive
                value={props.quickRanges.find(q => q.active)?.id ?? null}
                onChange={(_, value: string | null) => {
                    if (value) props.onQuickRange(value);
                }}
                sx={toolbarToggleSx}
            >
                {props.quickRanges.map(q => (
                    <ToggleButton
                        key={q.id}
                        value={q.id}
                        disabled={q.disabled}
                        aria-label={`Time range: ${q.label}`}
                    >
                        {q.label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
            {props.controls}
            <Select
                value={props.resolutionChoice ?? AUTO}
                onChange={event => {
                    const value = event.target.value;
                    props.onResolutionChange(
                        value === AUTO ? null : Number(value)
                    );
                }}
                size="small"
                variant="outlined"
                aria-label="Resolution"
                renderValue={value =>
                    value === AUTO
                        ? `Auto (${props.resolutionLabel})`
                        : props.resolutionLabel
                }
                sx={toolbarSelectSx}
            >
                <MenuItem value={AUTO} sx={{ fontSize: '12px' }}>
                    Auto
                </MenuItem>
                {RESOLUTION_OPTIONS.map(option => (
                    <MenuItem
                        key={option.value}
                        value={option.value}
                        sx={{ fontSize: '12px' }}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
            <Tooltip
                title={props.fullscreen ? 'Exit full screen' : 'Full screen'}
            >
                <IconButton
                    size="small"
                    onClick={props.onToggleFullscreen}
                    sx={{ p: 0.25 }}
                >
                    {props.fullscreen ? (
                        <FullscreenExitIcon sx={{ fontSize: 18 }} />
                    ) : (
                        <FullscreenIcon sx={{ fontSize: 18 }} />
                    )}
                </IconButton>
            </Tooltip>
        </Box>
    );
}
